import React from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { HomeData } from '../../../api/types/home-data/home-data';
import { LecturesResType } from '../../lectures/operations/get-lectures-data-operation';
import dateUtils from '../../../utils/date-format';
import {
  getDisplayText,
  LectureStatusType,
} from '../../../api/types/programs/course/lectures/lecture-status.type';
import { toast } from 'react-toastify';
import appApi from '../../../api/app-api';
import globalLectureBuilderStore from '../../../stores/global-lecture-builder-store';
import { LecturePageDialog } from '../../course/lectures/lecture-page-dialog/lecture-page-dialog';
import globalProgramStore from '../../../stores/program-store';
import globalCourseStore from '../../../stores/global-course-store';
import generateAndDownloadExcelFile from '../../../component/excel/generate-and-download-excel-file';
import { Button } from 'primereact/button';

interface TodayLecturesHomePageProps {
  homeData: HomeData | null;
}

export const TodayLecturesHomePage: React.FC<TodayLecturesHomePageProps> =
  observer(({ homeData }) => {
    // Display all lectures returned from the server
    const lectures = homeData?.lectures || [];

    const handleRowSelected = async (
      rowData: LecturesResType & { lectureId?: number },
    ) => {
      try {
        // Check if we have the required IDs
        if (
          !rowData.lectureId ||
          !rowData.courseId ||
          !rowData.programId ||
          !rowData.branchId
        ) {
          toast.error('לא ניתן לפתוח את פרטי ההרצאה - מידע חסר');
          return;
        }

        // Fetch program details to set up the global store
        const programResponse = await appApi.getProgramDetails(
          rowData.branchId,
          rowData.programId,
        );

        const programData = programResponse.data.body;

        // Set the program in the global store
        globalProgramStore.updateProgramDetails(rowData.programId, programData);

        // Fetch the full course details to get the lectures in the proper format
        const courseResponse = await appApi.getCourseDetails(
          rowData.programId,
          rowData.courseId,
        );

        const courseData = courseResponse.data.body;

        // Set up the global stores with the course data
        if (courseData.metadata) {
          globalCourseStore.addCourseMetadata(courseData.metadata);
        }
        if (courseData.details) {
          globalCourseStore.addCourseDetails(
            rowData.courseId,
            courseData.details,
          );
        }
        globalCourseStore.currentId = rowData.courseId;

        // Find the specific lecture by ID
        const lecture = courseData.details?.lectures?.find(
          (lec) => lec.item.id === rowData.lectureId,
        );

        if (!lecture) {
          toast.error('לא נמצאה הרצאה מתאימה');
          return;
        }

        // Set the lecture in the global store to open the dialog
        globalLectureBuilderStore.selectedLecture = lecture;
      } catch (error) {
        console.error('Error loading lecture details:', error);
        toast.error('שגיאה בטעינת פרטי ההרצאה');
      }
    };

    const getStatusTag = (status: LectureStatusType) => {
      // Based on the lecture status, return appropriate tags
      const displayText = getDisplayText(status);

      // You can customize colors based on status
      switch (status) {
        case LectureStatusType.COMPLETED_SUCCESSFULLY:
          return (
            <Tag
              style={{ width: '102px' }}
              severity="success"
              value={displayText}
            />
          );
        case LectureStatusType.ACTIVE:
        case LectureStatusType.NOT_STARTED:
          return (
            <Tag
              style={{ width: '102px' }}
              severity="info"
              value={displayText}
            />
          );
        case LectureStatusType.NOT_EXECUTED:
        case LectureStatusType.PENDING_APPROVAL:
          return (
            <Tag
              style={{ width: '102px' }}
              severity="danger"
              value={displayText}
            />
          );
        default:
          return (
            <Tag
              style={{ width: '102px' }}
              severity="warning"
              value={displayText}
            />
          );
      }
    };

    const handleExportToExcel = () => {
      // Create header row
      const headers = ['נושא', 'שעות', 'קורס', 'כיתה', 'סטטוס'];

      // Create data rows
      const dataRows = lectures.map((lecture) => [
        lecture.lectureSubject || 'שיעור',
        dateUtils.formatTimeRangeBetweenDates(
          lecture.startTime,
          lecture.endTime,
        ),
        lecture.courseName || 'לא צוין',
        lecture.className || 'לא צוין',
        getDisplayText(lecture.lectureStatus),
      ]);

      // Combine headers and data
      const excelData = [headers, ...dataRows];

      // Generate and download
      generateAndDownloadExcelFile(excelData);
    };

    return (
      <div
        style={{
          backgroundColor: '#ffffff',
          padding: '20px',
          height: '100%',
          borderRadius: '12px',
        }}
      >
        {!!globalLectureBuilderStore.selectedLecture && <LecturePageDialog />}
        <div
          style={{
            paddingBottom: '10px',
            color: '#185FD3',
            fontSize: '18px',
            marginBottom: '16px',
          }}
        >
          הרצאות היום ({lectures.length})
        </div>

        <DataTable
          value={lectures}
          emptyMessage={<div style={{ textAlign: 'right' }}>אין הרצאות</div>}
          scrollHeight="200px"
          scrollable
          dataKey="id"
          onRowClick={(e: DataTableRowClickEvent) => {
            handleRowSelected(e.data);
          }}
          tableStyle={{
            direction: 'rtl',
          }}
        >
          <Column
            field="lectureSubject"
            header="נושא"
            style={{ textAlign: 'right', fontSize: '14px' }}
            headerStyle={{
              textAlign: 'right',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
            body={(rowData: LecturesResType) => (
              <div style={{ fontSize: '13px' }}>
                {rowData.lectureSubject || 'שיעור'}
              </div>
            )}
          />

          <Column
            field="timeRange"
            header="שעות"
            style={{ textAlign: 'right', fontSize: '14px' }}
            headerStyle={{
              textAlign: 'right',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
            body={(rowData: LecturesResType) => (
              <div
                style={{
                  width: '85px',
                  direction: 'ltr',
                  textAlign: 'right',
                  fontSize: '13px',
                }}
              >
                {dateUtils.formatTimeRangeBetweenDates(
                  rowData.startTime,
                  rowData.endTime,
                )}
              </div>
            )}
          />

          <Column
            field="courseName"
            header="קורס"
            style={{ textAlign: 'right', fontSize: '14px' }}
            headerStyle={{
              textAlign: 'right',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
            body={(rowData: LecturesResType) => (
              <div style={{ fontSize: '13px' }}>
                {rowData.courseName || 'לא צוין'}
              </div>
            )}
          />

          <Column
            field="className"
            header="כיתה"
            style={{ textAlign: 'right', fontSize: '14px' }}
            headerStyle={{
              textAlign: 'right',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
            body={(rowData: LecturesResType) => (
              <div style={{ fontSize: '13px' }}>
                {rowData.className || 'לא צוין'}
              </div>
            )}
          />

          {/*<Column
            field="lectureStatus"
            header="סטטוס"
            style={{ textAlign: 'right', fontSize: '14px' }}
            headerStyle={{
              textAlign: 'right',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
            body={(rowData: LecturesResType) => (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  width: '120px',
                }}
              >
                {getStatusTag(rowData.lectureStatus)}
              </div>
            )}
          />*/}

          <Column
            header={() => (
              <Button
                style={{
                  height: '30px',
                  width: '30px',
                  backgroundColor: '#10b981',
                  borderColor: '#10b981',
                  marginLeft: '20px',
                }}
                icon="pi pi-file-excel"
                tooltip="ייצוא לאקסל"
                onClick={handleExportToExcel}
                disabled={lectures.length === 0}
              />
            )}
            style={{ textAlign: 'right', width: '30px' }}
          />
        </DataTable>
      </div>
    );
  });
